/**
 * Geometry for the contact sheet the script editor shows beside each sync point.
 *
 * The sheet is one image of evenly spaced frames. Both the worker that builds it
 * and the browser that slices it need to agree on the spacing, and neither can
 * import the other, so the convention lives here and the layout is arithmetic
 * rather than stored data — nothing to migrate when a sync point moves.
 */
export const filmstrip = {
  intervalSeconds: 2,
  columns: 12,
  cellWidth: 96,
} as const;

export interface FilmstripGrid {
  cells: number;
  columns: number;
  rows: number;
}

export function filmstripGrid(durationSeconds: number): FilmstripGrid {
  const cells = Math.max(1, Math.ceil(Math.max(1, durationSeconds) / filmstrip.intervalSeconds));
  const columns = Math.min(filmstrip.columns, cells);
  return { cells, columns, rows: Math.ceil(cells / columns) };
}

export interface FilmstripCell {
  /** CSS background-size, in pixels. */
  backgroundSize: string;
  /** CSS background-position, in pixels. */
  backgroundPosition: string;
  aspectRatio: number;
}

/**
 * Where to place the sheet so a box of `boxWidth` shows the frame at `seconds`.
 *
 * Returns CSS rather than cell indices because every caller wants the same two
 * declarations, and getting the scaling right at the call site is the fiddly part.
 */
export function filmstripCell(
  seconds: number,
  durationSeconds: number,
  sheetWidth: number,
  sheetHeight: number,
  boxWidth: number,
): FilmstripCell {
  const { cells, columns, rows } = filmstripGrid(durationSeconds);
  const index = Math.min(cells - 1, Math.max(0, Math.floor(seconds / filmstrip.intervalSeconds)));

  const cellWidth = sheetWidth / columns;
  const cellHeight = sheetHeight / rows;
  const scale = boxWidth / cellWidth;

  return {
    backgroundSize: `${Math.round(sheetWidth * scale)}px ${Math.round(sheetHeight * scale)}px`,
    backgroundPosition: `-${Math.round((index % columns) * cellWidth * scale)}px -${Math.round(
      Math.floor(index / columns) * cellHeight * scale,
    )}px`,
    aspectRatio: cellWidth / cellHeight,
  };
}
